import Page from "../Page";
import { useAccess } from "src/hooks/useAccess.hook";
import { 
  Card, 
  Stack, 
  Typography, 
  Box, 
  Chip, 
  IconButton,
  Tooltip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BaseTable } from "../BaseTable/BaseTable";
import { priceGroupListColumns } from "./PriceGroup.constants";
import { useList } from "src/hooks/useFetch.hook";
import { FEATURE } from "src/util/enums";
import { useNavigate } from "react-router-dom";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import refreshFill from "@iconify/icons-eva/refresh-fill";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.customShadows.z8,
  }
}));

export const PriceGroupList = () => {
  const navigate = useNavigate()
  const hasCreateAccess = useAccess(FEATURE.PRICEGROUP, "C");
  const isReadOnly = useAccess(FEATURE.PRICEGROUP, "R");
  const { data: priceGroups, isFetching, refetch } = useList("priceGroups");
  const [tableData, setTableData] = useState();
  const [searchValue, setSearchValue] = useState("");
  
  const rowOrCellClickRedirect = ({ id }) => {
    if(isReadOnly) return;
    navigate(`/pricegroup/${id}`);
  };

  useEffect(() => {
    if (!isFetching && priceGroups && Array.isArray(priceGroups)){
      let data = [...priceGroups].map(item => ({
        ...item,
        created: item.create_time || item.createdAt || item.created,
        updated: item.update_time || item.updatedAt || item.updated
      }));
      
      // Re-apply search filter if there's an active search
      if (searchValue) {
        const filteredData = data.filter((currentObj) => {
          const relevantFields = [
            "name",
            "description",
            "priceGroupType",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) =>
              key === "starttime"
                ? yearFirstDateTimeFormat(value).toString()
                : value ? value.toString().toLowerCase() : ""
            );
          return allValuesArr.some((value) =>
            value.includes(searchValue)
          );
        });
        setTableData(filteredData);
      } else {
        setTableData(data);
      }
    }
  }, [isFetching, priceGroups, searchValue]);

  const handleSearching = (event) => {
    const dataToSearchFrom = priceGroups || [];
    const searchTerm = event.target.value.trim().toString().toLowerCase();
    setSearchValue(searchTerm);
    
    if (searchTerm) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "name",
            "description",
            "priceGroupType",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) =>
              key === "starttime"
                ? yearFirstDateTimeFormat(value).toString()
                : value ? value.toString().toLowerCase() : ""
            );
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchTerm)
          );
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, currentObj];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
    } else {
      setTableData([...(priceGroups || [])]);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <Page
      {...(hasCreateAccess && { path: `/pricegroup/new` })}
      actionButtonText="Add Price Group"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Price Groups
          </Typography> */}
          <Typography variant="body1" sx={{ 
          color: (theme) => theme.palette.text.secondary,
          mb: 3
        }}>
          Manage Price Groups for different pricing schemes
        </Typography>
          {searchValue && (
            <Chip
              label={`${tableData?.length || 0} results`}
              color="primary"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
      </Stack>
      
      <StyledListCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CommonSearchbar
              placeholder="Search price groups"
              searchFunc={(e) => handleSearching(e)}
            />
          </Box>
          <Tooltip title="Refresh data">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isFetching}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Icon icon={refreshFill} width={20} height={20} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <BaseTable
          rows={tableData ?? []}
          columns={priceGroupListColumns(isReadOnly)}
          loading={isFetching}
          getRowId={(row) => row.priceGroupId}
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          pageSize={10}
          isReadOnly={isReadOnly}
          showExportCsvBtn={false}
          csvMorphemes="price-group"
          csvColumns={priceGroupListColumns(isReadOnly)}
          height={Math.max(200, (tableData?.length || 0) * 52 + 100)}
          initialState={{
            sorting: {
              sortModel: [{ field: 'created', sort: 'desc' }],
            },
          }}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-main': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-row': {
              height: '52px !important',
            },
            '& .MuiDataGrid-cell': {
              height: '52px !important',
            }
          }}
        />
      </StyledListCard>
    </Page>
  );
};
